import type { AdminFormParams } from "@/services/admin/adminApi"
import { roleService } from "@/services/role/roleApi"
import { useQuery } from "@tanstack/react-query"
import { Button, Form, Input, Select, Space, Switch } from "antd"
import type React from "react"
import { memo, useEffect } from "react"
import { DragModal } from "@/components/Modal"

interface AdminModalProps {
  open: boolean
  title: string
  loading: boolean
  initialValues?: any | null
  onOk: (values: AdminFormParams) => void
  onCancel: () => void
}

/**
 * Component Modal Thêm mới / Cập nhật tài khoản Admin
 */
const AdminModal: React.FC<AdminModalProps> = memo(({ open, title, loading, initialValues, onOk, onCancel }) => {
  const [form] = Form.useForm()

  // Lấy danh sách các vai trò (roles) hoạt động từ backend
  const { data: rolesResult } = useQuery({
    queryKey: ["roles_active_list", open],
    queryFn: () => roleService.list(),
    enabled: open,
  })

  // Định dạng danh sách vai trò cho component Select
  const roleOptions =
    rolesResult?.data?.map((item: any) => ({
      value: item.id,
      label: item.name,
    })) || []

  // Khởi tạo hoặc cập nhật dữ liệu biểu mẫu khi mở Modal
  useEffect(() => {
    if (!open) {
      return
    }

    if (initialValues) {
      // Khi chỉnh sửa: Gán dữ liệu hiện tại
      form.setFieldsValue({
        ...initialValues,
        // Chuyển đổi thông tin vai trò từ admin_roles thành mảng ID vai trò
        role: initialValues.role || initialValues.admin_roles?.map((item: any) => item.role_id) || [],
        is_active: initialValues.is_active === 1,
      })
    } else {
      // Khi thêm mới: Khởi tạo lại biểu mẫu
      form.resetFields()
      form.setFieldsValue({
        is_active: true,
        role: [],
      })
    }
  }, [open, initialValues, form])

  /**
   * Xử lý khi nhấn nút Xác nhận
   */
  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      const submitData: AdminFormParams = {
        ...values,
        id: initialValues?.id,
        is_active: values.is_active ? 1 : 0,
      }
      onOk(submitData)
    } catch (error: any) {
      const firstErrorField = error.errorFields?.[0]?.name
      if (firstErrorField) {
        form.scrollToField(firstErrorField)
      }
    }
  }

  /**
   * Xử lý khi đóng Modal xong
   */
  const handleAfterClose = () => {
    form.resetFields()
  }

  return (
    <DragModal
      centered
      title={title}
      open={open}
      onCancel={onCancel}
      width={600}
      destroyOnHidden={true}
      afterClose={handleAfterClose}
      footer={
        <Space>
          <Button onClick={onCancel}>Hủy</Button>
          <Button type="primary" onClick={handleOk} loading={loading}>
            Xác nhận
          </Button>
        </Space>
      }
      mask={{ closable: false }}
    >
      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
        <Form.Item
          name="full_name"
          label="Họ và tên"
          rules={[
            { required: true, message: "Vui lòng nhập họ và tên" },
            { max: 100, message: "Họ và tên không được vượt quá 100 ký tự" },
          ]}
        >
          <Input autoComplete="off" placeholder="Nhập họ và tên admin" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Vui lòng nhập địa chỉ email" },
            { type: "email", message: "Địa chỉ email không đúng định dạng" },
          ]}
        >
          <Input autoComplete="off" placeholder="Nhập địa chỉ email" disabled={!!initialValues?.id} />
        </Form.Item>

        <Form.Item
          name="username"
          label="Tên đăng nhập"
          rules={[
            { required: true, message: "Vui lòng nhập tên đăng nhập" },
            { max: 50, message: "Tên đăng nhập không được vượt quá 50 ký tự" },
            { pattern: /^[a-zA-Z0-9_]+$/, message: "Tên đăng nhập chỉ chứa chữ cái, số và dấu gạch dưới" },
          ]}
        >
          <Input autoComplete="off" placeholder="Nhập tên đăng nhập" disabled={!!initialValues?.id} />
        </Form.Item>

        <Form.Item
          name="password"
          label="Mật khẩu"
          rules={[
            { required: !initialValues?.id, message: "Vui lòng nhập mật khẩu" },
            { min: 6, message: "Mật khẩu phải chứa ít nhất 6 ký tự" },
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            placeholder={initialValues?.id ? "Để trống nếu không muốn đổi mật khẩu" : "Nhập mật khẩu"}
          />
        </Form.Item>

        <Form.Item name="role" label="Vai trò" rules={[{ required: true, message: "Vui lòng chọn vai trò" }]}>
          <Select mode="multiple" placeholder="Chọn vai trò" options={roleOptions} allowClear />
        </Form.Item>

        <Form.Item name="is_active" label="Trạng thái" valuePropName="checked">
          <Switch checkedChildren="Hoạt động" unCheckedChildren="Khóa" />
        </Form.Item>
      </Form>
    </DragModal>
  )
})

AdminModal.displayName = "AdminModal"

export default AdminModal
